/**
 * Branch Detail Page — Shows weeks for a branch, create/delete weeks.
 * Includes batch optimization with budget tier selector.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/routeOptimizerService';
import { BUDGET_OPTIONS, TIER_LABELS } from '../utils/constants';
import SettingsPanel from '../components/SettingsPanel';
import '../styles/RouteOptimizerApp.css';

export default function BranchDetail() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState([]);
  const [branchMeta, setBranchMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [newWeekName, setNewWeekName] = useState('');
  const [batchMode, setBatchMode] = useState('standard');
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchMessage, setBatchMessage] = useState('');
  const [activeTab, setActiveTab] = useState('weeks');
  const [branchSettings, setBranchSettings] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const s = await api.getBranchSummary(branchId);
      setSummary(s);
    } catch (err) {
      console.error('Failed to load branch summary:', err);
    }
  }, [branchId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadWeeks = useCallback(async () => {
    try {
      const data = await api.listWeeks(branchId);
      const sorted = (data.weeks || []).sort((a, b) => a.name.localeCompare(b.name));
      setWeeks(sorted);
      setBranchMeta({ branch_name: data.branch_name, company_id: data.company_id, company_name: data.company_name });
    } catch (err) {
      console.error('Failed to load weeks:', err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { loadWeeks(); }, [loadWeeks]);

  const loadBranchSettings = useCallback(async () => {
    try {
      const s = await api.getBranchSettings(branchId);
      setBranchSettings(s);
    } catch (err) {
      console.error('Failed to load branch settings:', err);
    }
  }, [branchId]);

  useEffect(() => { if (activeTab === 'settings') loadBranchSettings(); }, [activeTab, loadBranchSettings]);

  const handleCreateWeek = async (e) => {
    e.preventDefault();
    if (!newWeekName.trim()) return;
    await api.createWeek(branchId, newWeekName.trim());
    setNewWeekName('');
    await loadWeeks();
  };

  const handleDeleteWeek = async (wid) => {
    if (!window.confirm('Delete this week and all its data?')) return;
    await api.deleteWeek(wid);
    await loadWeeks();
  };

  const readyWeeks = weeks.filter(w => w.has_addresses && w.has_workers);

  const handleBatchOptimize = async () => {
    if (readyWeeks.length === 0) return;
    const label = TIER_LABELS[batchMode] || batchMode;
    if (!confirm(`This will optimize ${readyWeeks.length} week${readyWeeks.length !== 1 ? 's' : ''} using ${label}.\n\nJobs are processed sequentially and survive page close/logout.\n\nAre you sure?`)) return;
    setBatchRunning(true);
    setBatchMessage('');
    try {
      for (const w of readyWeeks) {
        await api.optimize(w.id, { mode: batchMode });
      }
      setBatchMessage(`${readyWeeks.length} optimizations queued. Worker will process them sequentially. You can safely close this page.`);
    } catch (err) {
      console.error('Batch optimize failed:', err);
      setBatchMessage(`Error: ${err.message}`);
    } finally {
      setBatchRunning(false);
    }
  };

  const statusIcon = (w) => {
    if (w.has_results) return '\u2713';
    if (w.has_distance_matrix) return '\u25CF';
    if (w.has_addresses) return '\u25CB';
    return '\u2014';
  };

  const statusColor = (w) => {
    if (w.has_results) return 'var(--ro-green)';
    if (w.has_distance_matrix) return 'var(--ro-orange)';
    if (w.has_addresses) return 'var(--ro-blue)';
    return 'var(--ro-border)';
  };

  const statusLabel = (w) => {
    if (w.has_results) return 'Results available';
    if (w.has_distance_matrix) return 'Matrix ready';
    if (w.has_addresses) return 'Data uploaded';
    return 'No data';
  };

  if (loading) return <div className="ro-loading">Loading...</div>;

  return (
    <div className="ro-dashboard">
      {/* Breadcrumb */}
      <div className="ro-breadcrumb">
        <a href="/routeoptimizer">Dashboard</a>
        <span className="ro-breadcrumb-sep">{'\u203A'}</span>
        {branchMeta.company_name && (
          <>
            {branchMeta.company_id ? (
              <a href={`/routeoptimizer/company/${branchMeta.company_id}`}>{branchMeta.company_name}</a>
            ) : (
              <span>{branchMeta.company_name}</span>
            )}
            <span className="ro-breadcrumb-sep">{'\u203A'}</span>
          </>
        )}
        <span className="ro-breadcrumb-current">{branchMeta.branch_name || 'Branch'}</span>
      </div>

      <div className="ro-header">
        <h1>{branchMeta.branch_name || 'Branch'}</h1>
        <p className="ro-subtitle">{weeks.length} week{weeks.length !== 1 ? 's' : ''} configured</p>
      </div>

      <div className="ro-tab-bar">
        <button
          className={`ro-tab ${activeTab === 'weeks' ? 'active' : ''}`}
          onClick={() => setActiveTab('weeks')}
        >
          Weeks
        </button>
        <button
          className={`ro-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="ro-card">
          <p className="ro-settings-info">
            These settings are the default for all new weeks created in this branch.
            Existing weeks keep their own settings.
          </p>
          {branchSettings ? (
            <SettingsPanel
              entityId={branchId}
              entityType="branch"
              settings={branchSettings}
              onRefresh={loadBranchSettings}
              onReset={async () => {
                const newSettings = await api.resetBranchSettings(branchId);
                setBranchSettings(newSettings);
                return newSettings;
              }}
              resetLabel="Reset to Company Defaults"
            />
          ) : (
            <div className="ro-loading">Loading settings...</div>
          )}
        </div>
      )}

      {activeTab === 'weeks' && <div className="ro-card">

        {/* Branch Summary Cards */}
        {summary && (
          <div className="ro-branch-summary-cards">
            <div className="ro-branch-summary-card">
              <div className="ro-branch-summary-value">{summary.total_addresses || 0}</div>
              <div className="ro-branch-summary-label">Addresses</div>
            </div>
            <div className="ro-branch-summary-card">
              <div className="ro-branch-summary-value">{summary.total_workers || 0}</div>
              <div className="ro-branch-summary-label">Employees</div>
            </div>
            <div className="ro-branch-summary-card">
              <div className="ro-branch-summary-value">{summary.ready_weeks}/{summary.total_weeks}</div>
              <div className="ro-branch-summary-label">Weeks Ready</div>
            </div>
            <div className="ro-branch-summary-card">
              <div className="ro-branch-summary-value">{summary.weeks_with_results}</div>
              <div className="ro-branch-summary-label">With Results</div>
            </div>
            {summary.last_optimization && (
              <div className="ro-branch-summary-card ro-branch-summary-card-highlight">
                <div className="ro-branch-summary-value">{Math.round(summary.last_optimization.best_miles).toLocaleString()} mi</div>
                <div className="ro-branch-summary-label">Last Best ({summary.last_optimization.best_tier})</div>
                <div className="ro-branch-summary-meta">{summary.last_optimization.date}</div>
              </div>
            )}
          </div>
        )}

        {/* Route Optimization — batch controls */}
        <div className="ro-section">
          <h4 className="ro-subsection-title">Route Optimization</h4>
          <div className="ro-batch-controls">
            <select
              className="ro-input ro-input-medium"
              value={batchMode}
              onChange={e => setBatchMode(e.target.value)}
              disabled={batchRunning}
            >
              {BUDGET_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              className="ro-btn ro-btn-primary"
              onClick={handleBatchOptimize}
              disabled={batchRunning || readyWeeks.length === 0}
            >
              {batchRunning ? 'Submitting...' : `Optimize ${readyWeeks.length} Week${readyWeeks.length !== 1 ? 's' : ''}`}
            </button>
          </div>

          {readyWeeks.length === 0 && weeks.length > 0 && (
            <p className="ro-warning">
              No weeks are ready for optimization (need addresses and workers).
            </p>
          )}

          {/* Success/error message */}
          {batchMessage && (
            <div className={batchMessage.startsWith('Error') ? 'ro-message-error' : 'ro-message-success'}>
              {batchMessage.replace(/^Error:\s*/, '')}
            </div>
          )}
        </div>

        {/* Week list */}
        {weeks.length === 0 ? (
          <p className="ro-empty">No weeks yet.</p>
        ) : (
          <div className="ro-week-list">
            {weeks.map(w => (
              <div key={w.id} className="ro-week-item" onClick={() => navigate(`/routeoptimizer/week/${w.id}`)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/routeoptimizer/week/${w.id}`); } }}>
                <div className="ro-week-status" style={{ color: statusColor(w), fontWeight: 700 }} title={statusLabel(w)} aria-label={statusLabel(w)}>{statusIcon(w)}</div>
                <div className="ro-week-info">
                  <h3>{w.name}</h3>
                  <span className="ro-meta">{w.created_at}</span>
                </div>
                <div className="ro-week-flags">
                  {w.has_addresses && <span className="ro-tag ro-tag-success">{w.address_count || ''} addresses</span>}
                  {w.has_workers && <span className="ro-tag ro-tag-success">{w.worker_count || ''} workers</span>}
                  {w.has_distance_matrix && <span className="ro-tag ro-tag-success">Matrix</span>}
                  {w.has_results && (
                    <span className="ro-tag ro-tag-success">
                      Results{w.result_tiers ? ` (${w.result_tiers.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')})` : ''}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="ro-btn ro-btn-danger ro-btn-sm"
                  onClick={(e) => { e.stopPropagation(); handleDeleteWeek(w.id); }}
                  aria-label={`Delete ${w.name}`}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}


        <form onSubmit={handleCreateWeek} className="ro-create-form">
          <input
            type="text"
            value={newWeekName}
            onChange={e => setNewWeekName(e.target.value)}
            placeholder="New week name (e.g., Week of Jan 20)"
            className="ro-input"
          />
          <button type="submit" className="ro-btn ro-btn-primary">Create Week</button>
        </form>
      </div>}
    </div>
  );
}

